// Central design tokens for AirProcurement.
// Values are kept in sync with the CSS custom properties in index.css.
// Keys are stable — feature components consume `S.*` directly.

const BRAND = "#4f46e5";
const INK = "#0f172a";
const INK_2 = "#475569";
const INK_3 = "#94a3b8";
const BORDER = "#e6e9f0";
const SURFACE = "#ffffff";

export const S = {
  page: {
    fontFamily: "var(--font-sans)",
    maxWidth: 1120,
    margin: "0 auto",
    padding: "0 24px 48px",
    color: INK,
  } as React.CSSProperties,

  // ---- App shell: left sidebar navigation ----
  shell: {
    display: "flex",
    alignItems: "flex-start",
    minHeight: "100vh",
  } as React.CSSProperties,

  sidebar: {
    position: "sticky",
    top: 0,
    alignSelf: "flex-start",
    height: "100vh",
    width: 256,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    background: SURFACE,
    borderRight: `1px solid ${BORDER}`,
  } as React.CSSProperties,

  sidebarBrand: {
    display: "flex",
    alignItems: "center",
    gap: 11,
    padding: "18px 18px 16px",
    borderBottom: `1px solid ${BORDER}`,
  } as React.CSSProperties,

  sidebarNav: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: "16px 12px",
  } as React.CSSProperties,

  navGroupLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: INK_3,
    padding: "0 12px",
    margin: "0 0 6px",
  } as React.CSSProperties,

  navBtn: (active: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    textAlign: "left",
    padding: "8px 12px",
    border: "none",
    borderRadius: 9,
    cursor: "pointer",
    fontWeight: active ? 700 : 500,
    fontSize: 13.5,
    lineHeight: 1.2,
    color: active ? "#4338ca" : INK_2,
    background: active ? "var(--brand-soft)" : "transparent",
    boxShadow: active ? "inset 3px 0 0 #4f46e5" : "none",
    transition: "background 0.15s, color 0.15s",
  }),

  sidebarFooter: {
    padding: "12px",
    borderTop: `1px solid ${BORDER}`,
  } as React.CSSProperties,

  mainCol: {
    flex: 1,
    minWidth: 0,
  } as React.CSSProperties,

  content: {
    maxWidth: 1120,
    margin: "0 auto",
    padding: "28px 32px 56px",
    color: INK,
  } as React.CSSProperties,

  card: {
    background: SURFACE,
    border: `1px solid ${BORDER}`,
    borderRadius: 14,
    padding: "20px 24px",
    marginBottom: 16,
    boxShadow: "var(--shadow-sm)",
  } as React.CSSProperties,

  h1: {
    fontSize: 24,
    fontWeight: 800,
    letterSpacing: "-0.02em",
    margin: "0 0 4px",
  } as React.CSSProperties,
  h2: {
    fontSize: 19,
    fontWeight: 700,
    letterSpacing: "-0.01em",
    margin: "0 0 16px",
  } as React.CSSProperties,
  h3: {
    fontSize: 15,
    fontWeight: 600,
    margin: "0 0 10px",
  } as React.CSSProperties,

  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 6,
    color: INK_2,
  } as React.CSSProperties,

  input: {
    width: "100%",
    padding: "9px 12px",
    border: `1px solid #d7dce6`,
    borderRadius: 9,
    fontSize: 14,
    boxSizing: "border-box" as const,
    marginBottom: 12,
    background: "#fff",
  } as React.CSSProperties,

  select: {
    width: "100%",
    padding: "9px 12px",
    border: `1px solid #d7dce6`,
    borderRadius: 9,
    fontSize: 14,
    boxSizing: "border-box" as const,
    marginBottom: 12,
    background: "#fff",
  } as React.CSSProperties,

  btn: {
    padding: "10px 22px",
    background: "linear-gradient(180deg, #4f46e5 0%, #4338ca 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 9,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(79,70,229,0.28)",
  } as React.CSSProperties,

  btnSm: {
    padding: "6px 13px",
    background: "linear-gradient(180deg, #4f46e5 0%, #4338ca 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 1px 4px rgba(79,70,229,0.25)",
  } as React.CSSProperties,

  btnGhost: {
    padding: "6px 13px",
    background: "#fff",
    color: INK_2,
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
  } as React.CSSProperties,

  statusBadge: (s: string): React.CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "3px 11px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.5,
    background:
      s === "Quoted"
        ? "#dcfce7"
        : s === "Awaiting" || s === "Sent"
          ? "#fef3c7"
          : s === "Expired"
            ? "#ffedd5"
            : s === "Closed"
              ? "#e0f2fe"
              : "#eef1f6",
    color:
      s === "Quoted"
        ? "#15803d"
        : s === "Awaiting" || s === "Sent"
          ? "#b45309"
          : s === "Expired"
            ? "#c2410c"
            : s === "Closed"
              ? "#0369a1"
              : INK_2,
  }),

  roleBadge: (role: string): React.CSSProperties => ({
    display: "inline-block",
    padding: "3px 9px",
    borderRadius: 6,
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase" as const,
    background:
      role === "admin"
        ? "#1e293b"
        : role === "buyer"
          ? BRAND
          : role === "manager"
            ? "#7c3aed"
            : role === "planner"
              ? "#0891b2"
              : role === "finance"
                ? "#059669"
                : "#64748b",
    color: "#fff",
  }),

  err: {
    color: "#b91c1c",
    fontSize: 13,
    margin: "8px 0",
  } as React.CSSProperties,
  ok: {
    color: "#15803d",
    fontSize: 13,
    margin: "8px 0",
  } as React.CSSProperties,
  muted: { color: INK_3, fontSize: 13 } as React.CSSProperties,
  grid2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  } as React.CSSProperties,
};
